import { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import * as R from 'ramda';
import { ReactNode, SyntheticEvent, useState } from 'react';
import useAutocompleteSearch from '../../Autocomplete/hooks/useAutocompleteSearch';
import {
  AsyncAutocompleteBundle,
  AsyncAutocompleteValue,
  UseAsyncAutocompleteParams,
} from '../asyncAutocomplete.types';
import {
  useAsyncAutocompleteList,
  useResolveAsyncAutocompleteValue,
} from '../queries/asyncAutocomplete.queries';
import { useAsyncAutocompleteCreateOption } from './useAsyncAutocompleteCreateOption';
import { useAsyncAutocompleteLoadError } from './useAsyncAutocompleteLoadError';

const SEARCH_DEBOUNCE_MS = 400;

const toIds = (value: string | string[] | null): string[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
};

export const useAsyncAutocomplete = <T, Multiple extends boolean | undefined = undefined>({
  source,
  fetcher,
  value,
  onChange,
  getOptionLabel,
  multiple,
  initialData,
  formatOptions,
  renderOption,
  getOptionId = (option: T) => (option as unknown as { id: string }).id,
  onOpen,
  onCreateOption,
}: UseAsyncAutocompleteParams<T, Multiple>): AsyncAutocompleteBundle<Multiple> => {
  const {
    url,
    queryField = 'query',
    limit = 20,
    order,
    defaultQuery,
    idsAlias = 'ids',
    enabled = true,
    eager = false,
  } = source;

  const { searchTerm, onInputChange: handleSearchInputChange } = useAutocompleteSearch({
    delay: SEARCH_DEBOUNCE_MS,
  });
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sessionSelections, setSessionSelections] = useState<T[]>([]);

  const valueIds = toIds(value as string | string[] | null);

  const listQuery = useAsyncAutocompleteList<T>({
    url,
    fetcher,
    order,
    limit,
    query: { ...defaultQuery, [queryField]: searchTerm },
    isEnabled: enabled && (eager || hasInteracted),
  });

  const locallyKnownIds = new Set([...(initialData ?? []), ...sessionSelections].map(getOptionId));
  const idsToResolve = valueIds.filter((id) => !locallyKnownIds.has(id));

  const resolveQuery = useResolveAsyncAutocompleteValue<T>({
    url,
    idsAlias,
    ids: idsToResolve,
    isEnabled: enabled && idsToResolve.length > 0,
  });

  useAsyncAutocompleteLoadError({
    listError: listQuery.query.error,
    valueError: resolveQuery.error,
  });

  const listItemsRaw = (listQuery.query.pagesData ?? []) as T[];
  const listItems = formatOptions ? formatOptions(listItemsRaw) : listItemsRaw;

  const resolvedRaw = resolveQuery.data ?? [];
  const resolvedItems = formatOptions ? formatOptions(resolvedRaw) : resolvedRaw;

  const mergedItems = R.uniqBy(getOptionId, [
    ...(initialData ?? []),
    ...resolvedItems,
    ...sessionSelections,
    ...listItems,
  ]);

  const optionsMap = new Map<string, T>();
  mergedItems.forEach((item) => optionsMap.set(getOptionId(item), item));

  const { createOptionId, createOptionLabel, trackInputText, createOption } =
    useAsyncAutocompleteCreateOption<T>({
      optionsMap,
      getOptionLabel,
      searchTerm,
      onCreateOption,
      isSearching: listQuery.query.isFetching,
    });

  const options = R.uniq([
    ...valueIds,
    ...listItems.map(getOptionId),
    ...(createOptionId ? [createOptionId] : []),
  ]);

  const resolveLabel = (id: string): string => {
    if (id === createOptionId) {
      return createOptionLabel;
    }
    const found = optionsMap.get(id);
    return (found && getOptionLabel(found)) || '-';
  };

  const resolveRenderOption = renderOption
    ? (id: string): ReactNode => {
        const found = optionsMap.get(id);
        return found ? renderOption(found) : null;
      }
    : undefined;

  const handleChange = (nextValue: AsyncAutocompleteValue<Multiple>) => {
    const nextIds = toIds(nextValue as string | string[] | null);

    if (createOptionId && nextIds.includes(createOptionId)) {
      createOption();
      return;
    }

    const chosen = nextIds
      .map((id) => optionsMap.get(id))
      .filter((item): item is T => item !== undefined);
    setSessionSelections((prev) => R.uniqBy(getOptionId, [...prev, ...chosen]));
    onChange(nextValue, chosen);
  };

  const onSearch = (
    event: SyntheticEvent,
    query: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    trackInputText(query);
    if (reason !== 'input') {
      return;
    }
    setHasInteracted(true);
    handleSearchInputChange(event, query, reason);
  };

  const onScrollEnd = () => {
    listQuery.query.fetchNextPage();
  };

  const handleOpen = (event: SyntheticEvent) => {
    setHasInteracted(true);
    onOpen?.(event);
  };

  return {
    options,
    listKey: JSON.stringify(listQuery.query.queryKey),
    value,
    multiple,
    onChange: handleChange,
    getOptionLabel: resolveLabel,
    renderOption: resolveRenderOption,
    loading: listQuery.query.isFetching,
    isResolvingValue: resolveQuery.isLoading,
    onSearch,
    onScrollEnd,
    onOpen: handleOpen,
    createOptionId,
  };
};
